import type { OGLRenderingContext } from 'ogl';

import { applyPrismInteractionMode } from './prismInteraction';
import { createPrismRenderLoop, type PrismPlaybackControls } from './prismRenderLoop';
import { createPrismRuntimeConfig } from './prismRuntimeConfig';
import { createPrismScene } from './prismScene';
import { observePrismResize } from './prismResize';
import { PrismPointerTracker } from './prismRotation';
import { startPrismVisibility, stopPrismVisibility } from './prismVisibility';
import type {
  PrismAnimationType,
  PrismFpsSampleRef,
  PrismMutableRef,
  PrismProgram,
  PrismScene,
} from './prismTypes';

interface MountPrismRendererInput {
  container: HTMLDivElement;
  height: number;
  baseWidth: number;
  animationType: PrismAnimationType;
  noise: number;
  offsetX: number;
  offsetY: number;
  transparent: boolean;
  hueShift: number;
  colorFrequency: number;
  hoverStrength: number;
  inertia: number;
  bloom: number;
  suspendWhenOffscreen: boolean;
  timeScale: number;
  maxDpr: number;
  onError?: (error: unknown) => void;
  glowRef: PrismMutableRef<number>;
  scaleRef: PrismMutableRef<number>;
  programRef: PrismMutableRef<PrismProgram | null>;
  glRef: PrismMutableRef<OGLRenderingContext | null>;
  /** Coverage state at mount, so a config-driven remount starts already paused. */
  initiallyPaused: boolean;
  onFpsSampleRef: PrismFpsSampleRef;
}

/** A mounted scene's lifetime: how to tear it down, and how to pause it while it lives. */
export interface MountedPrismRenderer {
  cleanup: () => void;
  playback: PrismPlaybackControls;
}

export function mountPrismRenderer({
  container,
  height,
  baseWidth,
  animationType,
  noise,
  offsetX,
  offsetY,
  transparent,
  hueShift,
  colorFrequency,
  hoverStrength,
  inertia,
  bloom,
  suspendWhenOffscreen,
  timeScale,
  maxDpr,
  onError,
  glowRef,
  scaleRef,
  programRef,
  glRef,
  initiallyPaused,
  onFpsSampleRef,
}: MountPrismRendererInput): MountedPrismRenderer | undefined {
  const runtimeConfig = createPrismRuntimeConfig({
    height,
    baseWidth,
    glow: glowRef.current,
    noise,
    offsetX,
    offsetY,
    transparent,
    scale: scaleRef.current,
    hueShift,
    colorFrequency,
    bloom,
    timeScale,
    hoverStrength,
    inertia,
  });
  const scene = createPrismScene({ container, runtimeConfig, transparent, maxDpr, onError });
  if (!scene) return undefined;

  programRef.current = scene.program;
  glRef.current = scene.gl;

  const resizeObserver = observePrismResize({
    scene,
    container,
    offsetX: runtimeConfig.offsetX,
    offsetY: runtimeConfig.offsetY,
    scaleRef,
  });
  const pointer = new PrismPointerTracker();
  const renderLoop = createPrismRenderLoop({
    scene,
    animationType,
    runtimeConfig,
    pointer,
    onFpsSampleRef,
    onError,
    initiallyPaused,
  });
  scene.gl.canvas.addEventListener('webglcontextlost', renderLoop.onContextLost, false);
  const stopInteraction = applyPrismInteractionMode({
    animationType,
    pointer,
    program: scene.program,
    startRAF: renderLoop.startRAF,
  });
  startPrismVisibility({
    container,
    suspendWhenOffscreen,
    startRAF: renderLoop.startRAF,
    stopRAF: renderLoop.stopRAF,
  });

  const cleanup = (): void => {
    unmountPrismRenderer({
      scene,
      container,
      suspendWhenOffscreen,
      renderLoopDestroy: renderLoop.destroy,
      resizeObserverDisconnect: () => resizeObserver.disconnect(),
      removeContextLostListener: () =>
        scene.gl.canvas.removeEventListener('webglcontextlost', renderLoop.onContextLost, false),
      stopInteraction,
      programRef,
      glRef,
    });
  };

  return {
    cleanup,
    playback: { pause: renderLoop.pause, resume: renderLoop.resume },
  };
}

interface UnmountPrismRendererInput {
  scene: PrismScene;
  container: HTMLDivElement;
  suspendWhenOffscreen: boolean;
  renderLoopDestroy: () => void;
  resizeObserverDisconnect: () => void;
  removeContextLostListener: () => void;
  stopInteraction: () => void;
  programRef: PrismMutableRef<PrismProgram | null>;
  glRef: PrismMutableRef<OGLRenderingContext | null>;
}

function unmountPrismRenderer({
  scene,
  container,
  suspendWhenOffscreen,
  renderLoopDestroy,
  resizeObserverDisconnect,
  removeContextLostListener,
  stopInteraction,
  programRef,
  glRef,
}: UnmountPrismRendererInput): void {
  renderLoopDestroy();
  resizeObserverDisconnect();
  removeContextLostListener();
  stopInteraction();
  stopPrismVisibility({ container, suspendWhenOffscreen });
  if (scene.gl.canvas.parentElement === container) container.removeChild(scene.gl.canvas);
  programRef.current = null;
  glRef.current = null;
}
